import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'sonner';
import { datasetService } from '../../services/DatasetService';
import { ROUTE_PATHS } from '../../constants/routePaths';

interface Dataset {
  dataset_id: number;
  name: string;
  description: string;
  file_path: string;
  type: 'singleturn' | 'multiturn';
  record_count: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
}

export function DatasetsPage() {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    setLoading(true);
    try {
      const data = await datasetService.getAllDatasets();
      console.log('📊 Fetched datasets:', data);
      console.log('📊 First dataset structure:', data[0]);
      setDatasets(data);
    } catch (err) {
      console.error('❌ Error fetching datasets:', err);
      toast.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDataset = () => {
    navigate(ROUTE_PATHS.DATASETS_ADD);
  };

  const handleDeleteClick = (dataset: Dataset) => {
    console.log('🗑️ Delete clicked for dataset:', dataset);
    console.log('🗑️ Dataset ID:', dataset.dataset_id);
    setSelectedDataset(dataset);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDataset) {
      console.error('❌ No dataset selected');
      return;
    }

    const datasetId = selectedDataset.dataset_id;

    console.log('🗑️ Attempting to delete dataset with ID:', datasetId);

    if (!datasetId) {
      console.error('❌ Dataset ID is undefined!');
      toast.error('Cannot delete dataset: ID is missing');
      setDeleteDialogOpen(false);
      setSelectedDataset(null);
      return;
    }

    setDeleting(true);
    try {
      await datasetService.deleteDataset(String(datasetId));
      setDatasets((prev) => prev.filter((d) => d.dataset_id !== datasetId));
      toast.success(`Dataset "${selectedDataset.name}" deleted successfully`);
    } catch (err: any) {
      console.error('❌ Delete error:', err);
      console.error('❌ Error response:', err.response);
      toast.error(err.response?.data?.message || 'Failed to delete dataset');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedDataset(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedDataset(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Datasets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your evaluation datasets
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDataset}
          sx={{
            bgcolor: '#2563eb',
            '&:hover': { bgcolor: '#1d4ed8' },
          }}
        >
          Create Dataset
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e2e8f0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} />
                  <Typography color="text.secondary" sx={{ mt: 2 }}>Loading datasets...</Typography>
                </TableCell>
              </TableRow>
            ) : datasets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No datasets found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              datasets.map((dataset) => (
                <TableRow key={dataset.dataset_id} hover>
                  <TableCell>{dataset.name}</TableCell>
                  <TableCell>{dataset.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={dataset.type === 'singleturn' ? 'Single Turn' : 'Multi Turn'}
                      size="small"
                      sx={{
                        bgcolor: dataset.type === 'singleturn' ? '#dbeafe' : '#fce7f3',
                        color: dataset.type === 'singleturn' ? '#1e40af' : '#be185d',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={dataset.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: dataset.is_active ? '#dcfce7' : '#fee2e2',
                        color: dataset.is_active ? '#166534' : '#991b1b',
                      }}
                    />
                  </TableCell>
                  <TableCell>{dataset.created_by}</TableCell>
                  <TableCell>{formatDate(dataset.created_at)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(dataset)}
                      sx={{ color: '#dc2626' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the dataset "{selectedDataset?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
